import React, { FunctionComponent, useState } from 'react'
import { FieldRenderProps } from 'react-final-form'
import { useIntl } from 'react-intl'
import { ViewStyle } from 'react-native'

import styled from 'styled-components/native'

import { BodyText, Size } from '../../../src/atoms/bodyText/BodyText'
import { BaseInput } from '../../../src/atoms/formElements/BaseInput'
import { FeedbackStarIcon } from '../../../src/atoms/icons/FeedbackStarIcon'
import { PressableOpacity } from '../../../src/atoms/pressableOpacity/PressableOpacity'
import { tID } from '../../utils'

export type FeedbackStarsProps = {
  label?: string
  name?: string
  value?: number
  onChange?: (value: number) => void
  error?: string
  starLabels?: string[]
  testID?: string
  periwinkle?: boolean
  customStyles?: {
    baseInput?: ViewStyle
    size?: number
    justifyStars?: string
    padding?: string
  }
}

const FeedbackStarsContainer = styled.View<{ justifyContent?: string; padding?: string }>(
  ({ theme, justifyContent = 'space-between', padding = '7px 26px 0px 26px' }) => ({
    flexDirection: 'row',
    justifyContent,
    padding,
    gap: theme.spacing['8px'],
  }),
)

const FeedbackStarContainer = styled.View({
  flexDirection: 'column',
  alignItems: 'center',
})

const StarLabelContainer = styled.View({
  paddingTop: '4px',
})

/**
 * Feedback Stars that is used for reviews
 */
export const FeedbackStars: FunctionComponent<FeedbackStarsProps> = ({
  label,
  name,
  value,
  onChange,
  error,
  starLabels = [],
  testID = 'VideoSessionFeedback',
  periwinkle = false,
  customStyles,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [hoverIndex, setHoverIndex] = useState(-1)
  const [isHovered, setIsHovered] = useState(false)
  const { formatMessage } = useIntl()

  const currentValue = value ?? selectedIndex

  const selectStar = (starValue: number) => {
    setSelectedIndex(starValue)
    onChange?.(starValue)
  }

  const getStars = () =>
    [1, 2, 3, 4, 5].map((star, index) => {
      const selected = star <= currentValue
      return (
        <FeedbackStarContainer key={index}>
          <PressableOpacity
            key={`feedbackStars-${star}`}
            testID={tID(`${testID}-FeedbackStars-${star}${selected ? ' selected' : ''}`)}
            accessibilityLabel={formatMessage(
              {
                defaultMessage: '{star, plural, one {1 star} other {{star} stars}}',
                description: 'Shows how many stars',
              },
              {
                star,
              },
            )}
            onPress={() => selectStar(star)}
            onHoverIn={() => {
              setHoverIndex(star)
              setIsHovered(true)
            }}
            onHoverOut={() => {
              setIsHovered(false)
              setHoverIndex(-1)
            }}
          >
            <FeedbackStarIcon
              isHighlighted={selected}
              isHovered={isHovered && hoverIndex >= star && !selected}
              periwinkle={periwinkle}
              width={customStyles?.size}
              height={customStyles?.size}
            />
          </PressableOpacity>
          {!!starLabels[index] && (
            <StarLabelContainer>
              <BodyText text={starLabels[index]} size={Size.CAPTION} />
            </StarLabelContainer>
          )}
        </FeedbackStarContainer>
      )
    })

  return (
    <BaseInput label={label} name={name} error={error} style={customStyles?.baseInput}>
      <FeedbackStarsContainer
        justifyContent={customStyles?.justifyStars}
        padding={customStyles?.padding}
        testID={tID(`${testID}-FeedbackStars`)}
      >
        {getStars()}
      </FeedbackStarsContainer>
    </BaseInput>
  )
}

export const FeedbackStarsRFF: FunctionComponent<FieldRenderProps<number>> = (props) => {
  const {
    input: { value, onChange, name },
    meta: { touched, error },
    label,
    starLabels,
    testID,
    periwinkle,
    customStyles,
  } = props
  return (
    <FeedbackStars
      label={label}
      name={name}
      value={value}
      onChange={onChange}
      error={touched && error}
      starLabels={starLabels}
      testID={testID}
      periwinkle={periwinkle}
      customStyles={customStyles}
    />
  )
}
